
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { collectErrors, errorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING,
  identityIntegrationsService,
  IDENTITY_INTEGRATION_STATUS_ACTIVE,
  IIdentityIntegration,
} from '@/services/identityIntegrationsService';

interface IStat {
  key: string;
  value: number | string;
  label: string;
  warn: boolean;
}

/**
 * The answer to "is this integration actually going to work", above the fold.
 *
 * The counts come from the integration we already hold; the verdict comes from
 * the read-only /validation endpoint, which runs the very same rules that
 * enabling does. Showing them here means an operator sees what is missing
 * before pressing Enable instead of discovering it one rejected request at a
 * time.
 */
@Component({ name: 'IntegrationSummary' })
export default class IntegrationSummary extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;

  protected loading: boolean = true;
  protected activatable: boolean = false;
  protected issues: string[] = [];
  protected loadError: string | null = null;

  created() {
    this.reload();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.reload();
  }

  get isActive(): boolean {
    return this.integration.status === IDENTITY_INTEGRATION_STATUS_ACTIVE;
  }

  get isAttributeMapped(): boolean {
    return this.integration.community_resolution.mode === COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING;
  }

  get stats(): IStat[] {
    const scope = this.integration.community_ids.length;
    const attributes = (this.integration.identity_mapping.attributes || []).length;
    const communityMappings = this.integration.community_resolution.mapping_count;

    return [
      {
        key: 'communities',
        value: scope,
        label: this.$t('identityIntegrations.summary.communities') as string,
        // An empty scope authenticates nobody, whatever else is configured.
        warn: scope === 0,
      },
      {
        key: 'attributes',
        value: attributes,
        label: this.$t('identityIntegrations.summary.attributes') as string,
        warn: attributes === 0,
      },
      {
        key: 'communityMappings',
        value: communityMappings,
        label: this.$t('identityIntegrations.summary.communityMappings') as string,
        // Only meaningful under attribute-mapped resolution; under fixed the
        // rows are inert, so zero is not a problem worth flagging.
        warn: this.isAttributeMapped && communityMappings === 0,
      },
      {
        key: 'identities',
        value: this.integration.linked_identity_count,
        label: this.$t('identityIntegrations.summary.identities') as string,
        warn: false,
      },
    ];
  }

  get healthClass(): string {
    if (this.loading || this.loadError) {
      return 'ii-health--neutral';
    }

    if (this.issues.length) {
      return this.isActive ? 'ii-health--error' : 'ii-health--warn';
    }

    return 'ii-health--ok';
  }

  get healthIcon(): string {
    if (this.loadError) {
      return 'mdi-help-circle-outline';
    }

    if (this.issues.length) {
      return this.isActive ? 'mdi-alert-circle-outline' : 'mdi-alert-outline';
    }

    return 'mdi-check-circle-outline';
  }

  get healthIconColor(): string {
    if (this.loadError) {
      return 'grey';
    }

    if (this.issues.length) {
      return this.isActive ? 'error' : 'warning';
    }

    return 'success';
  }

  /**
   * An active integration with issues is a live incident — logins are failing
   * right now. An inactive one is simply not ready yet. Same rules, very
   * different urgency, so they do not get the same wording or colour.
   */
  get healthTitle(): string {
    if (this.loading) {
      return this.$t('identityIntegrations.summary.checking') as string;
    }

    // "No issues" and "could not ask" are not the same answer. Reporting a
    // failed check as a clean bill of health is the one thing this panel must
    // never do — an operator would read it as permission to stop looking.
    if (this.loadError) {
      return this.$t('identityIntegrations.summary.checkFailed') as string;
    }

    if (!this.issues.length) {
      return this.isActive
        ? (this.$t('identityIntegrations.summary.healthyActive') as string)
        : (this.$t('identityIntegrations.summary.readyToEnable') as string);
    }

    return this.isActive
      ? (this.$t('identityIntegrations.summary.brokenActive') as string)
      : (this.$t('identityIntegrations.summary.notReady') as string);
  }

  get healthHint(): string {
    return this.isActive
      ? (this.$t('identityIntegrations.summary.healthyActiveHint') as string)
      : (this.$t('identityIntegrations.summary.readyToEnableHint') as string);
  }

  issueLabel(issue: string): string {
    const key = 'identityIntegrations.errors.' + issue;

    return this.$te(key) ? (this.$t(key) as string) : issue;
  }

  async reload() {
    this.loading = true;
    this.loadError = null;
    try {
      const validation = await identityIntegrationsService.validation(this.integration.id);
      this.activatable = validation.activatable;
      this.issues = validation.issues || [];
    } catch (error) {
      this.issues = [];
      this.loadError = errorMessage(this, collectErrors(error)[0]);
    } finally {
      this.loading = false;
    }
  }
}
